import { modulePath } from '@nexus/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import type { SessionInfo } from '../api/types';
import { safePath } from '../lib/paths';
import { useModules } from '../modules/context';
import type { WebModule } from '../modules/define';
import { buildNavigation } from '../modules/navigation';
import { useSession } from '../session/session';

/** One language of a note: what changed and how to use it right away. */
export interface ReleaseNoteText {
  title: string;
  text: string;
  howTo?: string;
}

/**
 * A change for users, from the "Was ist neu" block of a commit message, read when the web app is
 * built (docs/architecture/whats-new.md).
 */
export interface ReleaseNote {
  /** Commit and position of the note in it. */
  id: string;
  /** ISO time of the commit. */
  date: string;
  de: ReleaseNoteText;
  en: ReleaseNoteText;
  /** Page in the app where the change can be tried. */
  link?: string;
}

/** Loads the notes of the build; a separate chunk, so the start page does not wait for it. */
export type ReleaseNotesLoader = () => Promise<{ default: readonly ReleaseNote[] }>;

/** A build without notes, e.g. in tests. */
export const noReleaseNotes: ReleaseNotesLoader = () => Promise.resolve({ default: [] });

export const ReleaseNotesContext = createContext<ReleaseNotesLoader>(noReleaseNotes);

/**
 * The notes the person should see, newest first. A note whose link leads into a module is only
 * for people who can open that module in the active company; the others are for everyone.
 */
export function visibleNotes(
  notes: readonly ReleaseNote[],
  modules: readonly WebModule[],
  session: SessionInfo,
): ReleaseNote[] {
  const open = new Set(buildNavigation(modules, session).map((module) => module.id));
  const active = new Set(session.modules);
  const ownerOf = (path: string) =>
    modules
      .map((module) => module.manifest)
      .filter((manifest) => {
        const base = modulePath(manifest.id);
        return path === base || path.startsWith(`${base}/`) || path.startsWith(`${base}?`);
      })
      // The longest base wins, so a submodule beats its parent.
      .sort((a, b) => modulePath(b.id).length - modulePath(a.id).length)[0];
  return notes
    .filter((note) => {
      const link = note.link && safePath(note.link);
      const owner = link ? ownerOf(link) : undefined;
      if (!owner) return true;
      return active.has(owner.id) && open.has(owner.parent ?? owner.id);
    })
    .sort((a, b) => b.date.localeCompare(a.date));
}

/** The text of a note in the language of the app; German is the source. */
export function noteText(note: ReleaseNote, language: string): ReleaseNoteText {
  return language.startsWith('en') ? note.en : note.de;
}

/** The notes of the build the person may see. Suspends while the chunk loads. */
export function useReleaseNotes(): ReleaseNote[] {
  const load = useContext(ReleaseNotesContext);
  const modules = useModules();
  const session = useSession();
  const { data } = useSuspenseQuery({
    queryKey: ['release-notes'],
    queryFn: async () => (await load()).default,
    staleTime: Infinity,
  });
  return useMemo(() => visibleNotes(data, modules, session), [data, modules, session]);
}
